import React, { useState } from 'react';
import { Grid, TableColumnResizing, TableHeaderRow, TableSelection } from '@devexpress/dx-react-grid-material-ui';
import { DataTypeProvider, IntegratedSelection, SelectionState, SortingState } from '@devexpress/dx-react-grid';
import moment from 'moment/moment';
import classnames from 'classnames';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import tooltipStyles from 'shared/components/andtComponents/Tooltip.module.scss';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner';
import {
  CustomClickableFormatter,
  CustomFormatterWithTooltipOnlyIfLongText,
  CustomHeaderCell,
  CustomSelectionCell,
} from 'shared/components/andtComponents/TableComponents/TableComponents';
import toast from 'shared/components/andtComponents/Toast';
import TableWrapper from 'shared/components/tables/TableWrapper';
import {
  GET_USERS_QUERY_PARAMS,
  getColumns,
  getDefaultSorting,
  getSortingColumns,
  TAB_PATHS,
  USER_ROLES_COLUMNS,
} from '../../consts';
import RemoveButton from '../../components/RemoveButton';
import RolesNoData from 'users/containers/Organization/components/EmptyStates/RolesNoData.jsx';
import { OrganizationEntityCategory, Action } from '@anodot-cost/rbac-client';
import ReadOnlyDisplayWrapper from 'shared/components/ReadOnlyDisplayWrapper';
import styles from './UserRolesTabTable.module.scss';

const onRoleNameClick = (row) => {
  const currentUrl = new URL(window.location.href);
  if (currentUrl.pathname.endsWith(`/${TAB_PATHS.USERS}`)) {
    currentUrl.pathname = currentUrl.pathname.replace(`/${TAB_PATHS.USERS}`, `/${TAB_PATHS.ROLES}`);
  } else {
    currentUrl.pathname = `${currentUrl.pathname}/${TAB_PATHS.ROLES}`;
  }
  currentUrl.searchParams.set(GET_USERS_QUERY_PARAMS.SEARCH.name, row?.displayName);
  window.open(currentUrl.toString(), '_blank');
};

const UserRolesTabTable = ({
  userRoles,
  userDefaultSharingRoleId,
  selectedUserRoles,
  setSelectedUserRoles,
  removeRoleClicked,
  setDefaultRoleForSharing,
}) => {
  const [localColumnsWidth, setLocalColumnsWidth] = useState(
    Object.values(USER_ROLES_COLUMNS).map((c) => ({ columnName: c.columnName, width: c.width })),
  );
  const [updateDefaultRoleForSharingStatus, setUpdateDefaultRoleForSharingStatus] = useState({
    isUpdating: false,
    roleId: null,
  });

  const renderRemoveButton = (props) => (
    <ReadOnlyDisplayWrapper isHide={false} category={OrganizationEntityCategory.UsersManagement} action={Action.Update}>
      <RemoveButton props={props} removeClicked={() => removeRoleClicked(props.row)} />
    </ReadOnlyDisplayWrapper>
  );

  const getPinToolTip = (roleId) => {
    const isPinned = userDefaultSharingRoleId && userDefaultSharingRoleId === roleId;
    if (isPinned) {
      return (
        <div className={styles.defaultRolePinTooltip}>
          <div className={styles.tooltipHeader}>
            <GenerateIcon iconName={ICONS.unpin.name} className={styles.pinnedTooltipIcon} />
            <div>Unpin as the default role for sharing</div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.defaultRolePinTooltip}>
        <div className={styles.tooltipHeader}>
          <GenerateIcon iconName={ICONS.pin.name} className={styles.unpinnedTooltipIcon} />
          <div>Pin as the default role for sharing</div>
        </div>
        <div className={styles.tooltipDescription}>
          Each entity created by this user will be shared for listing and viewing with users that are assigned to this role.
        </div>
      </div>
    );
  };

  const updateDefaultRoleForSharing = async (e, roleId) => {
    e.stopPropagation();
    if (updateDefaultRoleForSharingStatus.isUpdating) {
      toast.info('Please wait, the default role for sharing is being updated.');
      return;
    }
    setUpdateDefaultRoleForSharingStatus({ isUpdating: true, roleId });
    try {
      await setDefaultRoleForSharing(roleId);
    } finally {
      setUpdateDefaultRoleForSharingStatus({ isUpdating: false, roleId: null });
    }
  };

  const renderDefaultRolePinButton = (props) => {
    const roleId = props.row?.id;

    return (
      <div>
        <Tooltip
          title={getPinToolTip(roleId)}
          placement="bottom"
          arrow={true}
          classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
        >
          <span className={styles.defaultRolePinIconWrapper}>
            {updateDefaultRoleForSharingStatus.isUpdating && updateDefaultRoleForSharingStatus.roleId === roleId ? (
              <Spinner size={SPINNER_SIZES.X_SMALL} />
            ) : (
              <GenerateIcon
                onClick={(e) => updateDefaultRoleForSharing(e, roleId)}
                iconName={ICONS.pin.name}
                className={classnames(styles.defaultRolePinIcon, {
                  [styles.selectedDefaultRole]: userDefaultSharingRoleId && userDefaultSharingRoleId === roleId,
                  [styles.roleUpdating]: updateDefaultRoleForSharingStatus.isUpdating,
                })}
              />
            )}
          </span>
        </Tooltip>
      </div>
    );
  };

  return (
    userRoles && (
      <Grid rows={userRoles} columns={getColumns(USER_ROLES_COLUMNS)} automationid="user-roles-tab-list">
        <SortingState
          columnExtensions={getSortingColumns(USER_ROLES_COLUMNS)}
          defaultSorting={getDefaultSorting(USER_ROLES_COLUMNS)}
        />
        <SelectionState selection={selectedUserRoles} onSelectionChange={setSelectedUserRoles} />
        <IntegratedSelection />
        <DataTypeProvider
          for={[USER_ROLES_COLUMNS.ROLE_NAME.columnName]}
          formatterComponent={(props) => (
            <CustomClickableFormatter {...props} onClick={() => onRoleNameClick(props?.row)} />
          )}
        />
        <DataTypeProvider
          for={[USER_ROLES_COLUMNS.ASSIGNED_AT.columnName]}
          formatterComponent={({ row }) => (
            <CustomFormatterWithTooltipOnlyIfLongText
              value={row?.assignedAt ? moment(row?.assignedAt).format('MMM DD YYYY, hh:mm:ss A') : null}
              isSmallText
            />
          )}
        />
        <DataTypeProvider
          for={[USER_ROLES_COLUMNS.ASSIGNED_BY.columnName]}
          formatterComponent={(props) => <CustomFormatterWithTooltipOnlyIfLongText isSmallText {...props} />}
        />
        <DataTypeProvider
          for={[USER_ROLES_COLUMNS.REMOVE_USER_ROLE.columnName]}
          formatterComponent={renderRemoveButton}
        />
        <DataTypeProvider
          for={[USER_ROLES_COLUMNS.DEFAULT_ROLE_FOR_SHARING.columnName]}
          formatterComponent={renderDefaultRolePinButton}
        />
        <TableWrapper virtual noDataCellComponent={RolesNoData} height="auto" />
        <TableSelection showSelectAll cellComponent={CustomSelectionCell} />
        <TableColumnResizing
          resizingMode="nextColumn"
          columnWidths={localColumnsWidth}
          onColumnWidthsChange={setLocalColumnsWidth}
        />
        <TableHeaderRow showSortingControls cellComponent={CustomHeaderCell} />
      </Grid>
    )
  );
};

export default UserRolesTabTable;
